import { useState } from 'react';
import { Mail, KeyRound, UserRound, Phone, Send, Check, Loader2, Fingerprint, ShieldCheck } from 'lucide-react';
import {
  memberAuth, useTenant, passkeySupported, runPasskeyEnroll, runPasskeyGet,
  parsePasskeyChallenge, TenantLogo, type PasskeyStepUp,
} from '@fitboxx/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';

export default function Login({ onLoggedIn }: { onLoggedIn?: () => void }) {
  const { tenant } = useTenant();
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  // The studio's FitBOXX subscription is suspended (backend 402
  // subscription_inactive): set from ?message= when the shared API client
  // ended the session, or by a 402 on sign-in. Members are told neutrally —
  // the studio's billing is not theirs to sort out.
  const [unavailable, setUnavailable] = useState(
    () => new URLSearchParams(window.location.search).get('message') === 'subscription_inactive');
  // GateBOXX device trust: set ONLY when the backend refuses because this
  // browser isn't approved for the studio (enforce mode).
  const [devicePairing, setDevicePairing] = useState<{ code: string; detail: string } | null>(null);
  // First-time requesters may be asked who they are (toggleable in the
  // console) — the admin calls the number back before approving this browser.
  const [collectContact, setCollectContact] = useState(false);
  const [contactRequired, setContactRequired] = useState(false);
  const [contact, setContact] = useState({ name: '', phone: '', email: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');
  // GateBOXX passkey (WebAuthn) step-up. 'enrolling' = binding a passkey after
  // a successful member sign-in (require_passkey=preferred); 'assert' = a
  // required tenant demanding proof. Idle and inert for every tenant on
  // require_passkey=off (all of them today).
  const [passkeyStep, setPasskeyStep] = useState<'idle' | 'enrolling' | 'assert'>('idle');
  const [passkeyError, setPasskeyError] = useState('');

  async function submitContact() {
    const name = contact.name.trim(), phone = contact.phone.trim(), cemail = contact.email.trim();
    if (contactRequired && !(name && phone && cemail)) {
      setContactError('Please fill in your name, phone number and email.');
      return;
    }
    if (!(name || phone || cemail)) {
      setContactError('Enter at least your name and phone number.');
      return;
    }
    setContactSending(true);
    setContactError('');
    try {
      // Plain fetch on purpose: the identity is this browser's httpOnly cookie
      // (rides automatically, same-origin) — no auth token exists yet.
      const r = await fetch('/api/v1/auth/device-trust/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, email: cemail }),
      });
      const d = await r.json().catch(() => ({} as Record<string, unknown>));
      if (r.ok && d.stored) {
        setContactSent(true);
      } else {
        setContactError('Could not send your details — read the code above to the front desk instead.');
      }
    } catch {
      setContactError('Could not send your details — read the code above to the front desk instead.');
    } finally {
      setContactSending(false);
    }
  }

  async function request() {
    setBusy(true); setErr(''); setInfo('');
    try {
      await memberAuth.requestOTP({ email });
      setInfo(`We sent a 6-digit code to ${email}.`);
      setStep('verify');
    } catch (e) {
      if ((e as { body?: { error?: string } })?.body?.error === 'subscription_inactive') setUnavailable(true);
      else setErr('Could not send the code. Check the email and try again.');
    }
    finally { setBusy(false); }
  }

  // One verify attempt. On success it also runs the passkey ENROL ceremony when
  // an approved-but-unenrolled device is offered one (require_passkey=preferred),
  // then continues. Throws on refusal so the caller can drive passkey step-up.
  async function doVerify(passkey?: PasskeyStepUp) {
    const r = await memberAuth.verifyOTP({
      email, code, first_name: firstName,
      ...(passkey ? { passkey_assertion: passkey.assertion, rp_id: passkey.rpId, origin: passkey.origin } : {}),
    });
    localStorage.setItem('fitboxx.member.access', r.access);
    if (r.device_trust?.passkey_enroll && passkeySupported()) {
      // Bind a passkey to this browser now; sign-in already succeeded, so any
      // failure here just leaves the device un-enrolled for next time.
      setPasskeyStep('enrolling');
      try {
        await runPasskeyEnroll(email.trim());
      } catch {
        /* dismissed OS prompt or no platform authenticator — fine */
      } finally {
        setPasskeyStep('idle');
      }
    }
    onLoggedIn?.();
    // Hard reload kicks off TenantProvider re-init with the new token.
    location.reload();
  }

  async function verify() {
    setBusy(true); setErr(''); setDevicePairing(null); setContactSent(false); setContactError(''); setPasskeyError('');
    try {
      await doVerify();
    } catch (e) {
      const body = (e as { body?: {
        error?: string; reason?: string; detail?: string; pairing_code?: string;
        collect_contact?: boolean; contact_required?: boolean;
        passkey_required?: boolean; passkey_challenge?: unknown;
      } })?.body || {};
      // Passkey step-up (require_passkey=required). NOTE: the member OTP is
      // single-use and is consumed by verify_otp BEFORE the device-trust gate
      // runs, so a resubmit re-sends a spent code and the backend answers
      // invalid_or_expired — this assert branch cannot converge for the OTP
      // flow without a backend reorder. It stays here for parity with the
      // staff console and is dead code while require_passkey=off (all tenants).
      const wantsPasskey = body?.reason === 'passkey_required' || !!body?.passkey_required;
      const challenge = parsePasskeyChallenge(body?.passkey_challenge);
      if (wantsPasskey && challenge && passkeySupported()) {
        setPasskeyStep('assert');
        try {
          const assertion = await runPasskeyGet(challenge);
          if (assertion) {
            await doVerify({ assertion, rpId: window.location.hostname, origin: window.location.origin });
            setPasskeyStep('idle');
            return;
          }
          setPasskeyError('The passkey prompt was dismissed. Use your passkey to finish signing in.');
        } catch {
          setPasskeyError('Your passkey could not be verified on this device. Try again, or ask the front desk.');
        }
        return;
      }
      if (body.error === 'device_not_trusted') {
        // The code was RIGHT — the browser needs admin approval. Saying
        // "code invalid" here would send the member in circles.
        setDevicePairing({
          code: body.pairing_code || '',
          detail: typeof body.detail === 'string' ? body.detail : '',
        });
        setCollectContact(!!body.collect_contact);
        setContactRequired(!!body.contact_required);
      } else if (body.error === 'subscription_inactive') {
        setUnavailable(true);
      } else {
        setErr('That code is invalid or expired. Try again.');
      }
    }
    finally { setBusy(false); }
  }

  // Manual passkey retry: re-run verify so the server issues a FRESH single-use
  // challenge under a fresh user gesture from the button click.
  function retryPasskey() {
    setPasskeyError('');
    void verify();
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <TenantLogo tenant={tenant} size={76} className="mx-auto mb-3" />
          <div className="text-2xl font-semibold tracking-tight">{tenant?.display_name || 'FitBOXX'}</div>
          <div className="text-sm text-muted-foreground mt-1">Member portal</div>
        </div>
        <Card className="p-6 space-y-4">
          <div>
            <h1 className="text-lg font-semibold">
              {step === 'request' ? 'Welcome back' : 'Enter your code'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {step === 'request'
                ? 'Sign in with your email — no password needed.'
                : `We sent a 6-digit code to ${email}.`}
            </p>
          </div>

          {unavailable && (
            <div className="text-sm rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-destructive">
              The member portal for this studio is unavailable right now. Please contact the studio.
            </div>
          )}

          {devicePairing && (
            <div className="text-sm rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 space-y-1.5">
              <div className="font-semibold">This browser needs approval</div>
              <p>
                {devicePairing.detail ||
                  'This browser is not approved for this studio yet. Ask the front desk to approve it.'}
              </p>
              {devicePairing.code && (
                <div>
                  Approval code:{' '}
                  <span className="font-mono font-bold tracking-widest">{devicePairing.code}</span>
                </div>
              )}
            </div>
          )}
          {/* Who's asking — shown with the refusal when the studio wants
              contact details collected, so the front desk can call back and
              confirm identity before approving this browser. */}
          {devicePairing && collectContact && !contactSent && (
            <div className="rounded-md border bg-muted/40 p-3 space-y-2.5">
              <p className="text-sm font-medium">
                Tell the front desk who you are
                {contactRequired ? '' : ' (optional)'}
              </p>
              <p className="text-xs text-muted-foreground">
                They may call this number to confirm it's really you before
                approving this browser.
              </p>
              <div className="relative">
                <UserRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Your name" className="pl-9" value={contact.name}
                       disabled={contactSending}
                       onChange={(e) => { setContact(c => ({ ...c, name: e.target.value })); if (contactError) setContactError(''); }} />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Phone number" className="pl-9" value={contact.phone}
                       disabled={contactSending} inputMode="tel"
                       onChange={(e) => { setContact(c => ({ ...c, phone: e.target.value })); if (contactError) setContactError(''); }} />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Email address" className="pl-9" type="email" value={contact.email}
                       disabled={contactSending}
                       onChange={(e) => { setContact(c => ({ ...c, email: e.target.value })); if (contactError) setContactError(''); }} />
              </div>
              {contactError && <p className="text-xs text-destructive">{contactError}</p>}
              <Button type="button" variant="secondary" className="w-full"
                      onClick={() => void submitContact()} disabled={contactSending}>
                {contactSending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Send className="mr-2 h-4 w-4" />}
                Send to front desk
              </Button>
            </div>
          )}
          {devicePairing && contactSent && (
            <div className="rounded-md border border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-900 p-3 flex items-start gap-2">
              <Check className="h-4 w-4 mt-0.5 text-green-600" />
              <p className="text-sm text-green-800 dark:text-green-300">
                Details sent. The front desk can now see who's asking — mention
                code <span className="font-mono font-semibold">{devicePairing.code}</span> if
                they call. Try signing in again once they approve this browser.
              </p>
            </div>
          )}
          {/* Passkey enrolment in progress — binding a hardware key after a
              successful sign-in. Brief; we reload as soon as the prompt resolves. */}
          {passkeyStep === 'enrolling' && (
            <div className="rounded-md border bg-muted/40 p-3 flex items-start gap-2.5">
              <Loader2 className="h-5 w-5 mt-0.5 animate-spin text-primary" />
              <div>
                <p className="text-sm font-medium">Securing this device…</p>
                <p className="text-xs text-muted-foreground">
                  Confirm with your device (Face ID, fingerprint, or PIN) to
                  remember it with a passkey.
                </p>
              </div>
            </div>
          )}

          {/* Passkey step-up — a passkey-required studio must see proof from
              the passkey enrolled on this device. */}
          {passkeyStep === 'assert' && (
            <div className="rounded-md border bg-muted/40 p-3 space-y-2.5">
              <div className="flex items-start gap-2.5">
                <ShieldCheck className="h-5 w-5 mt-0.5 text-primary" />
                <div>
                  <p className="text-sm font-medium">This studio requires a passkey</p>
                  <p className="text-xs text-muted-foreground">
                    Verify with the passkey enrolled on this device to finish
                    signing in.
                  </p>
                </div>
              </div>
              {passkeyError && <p className="text-xs text-destructive">{passkeyError}</p>}
              <Button type="button" variant="secondary" className="w-full"
                      onClick={() => retryPasskey()} disabled={busy}>
                {busy
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Fingerprint className="mr-2 h-4 w-4" />}
                Verify with your passkey
              </Button>
            </div>
          )}

          {err && <div className="text-sm rounded-md bg-destructive/10 text-destructive px-3 py-2">{err}</div>}
          {info && step === 'request' && <div className="text-sm rounded-md bg-muted px-3 py-2">{info}</div>}

          {step === 'request' ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="m-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="m-email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required disabled={busy}
                    placeholder="you@example.com" className="pl-9"
                  />
                </div>
              </div>
              <Button onClick={() => void request()} disabled={busy || !email} className="w-full">
                {busy ? 'Sending…' : 'Send sign-in code'}
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="m-code">6-digit code</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="m-code" inputMode="numeric" autoComplete="one-time-code"
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required disabled={busy} placeholder="123456" className="pl-9 tracking-widest"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-first">First name <span className="text-muted-foreground font-normal">(new members)</span></Label>
                <Input
                  id="m-first" value={firstName}
                  onChange={(e) => setFirstName(e.target.value)} disabled={busy}
                  placeholder="Optional if you've signed in before"
                />
              </div>
              <Button onClick={() => void verify()} disabled={busy || code.length < 4} className="w-full">
                {busy ? 'Verifying…' : 'Verify and continue'}
              </Button>
              <div className="text-center text-sm">
                <button type="button" className="text-primary hover:underline"
                  onClick={() => { setStep('request'); setCode(''); setInfo(''); }}>
                  Use a different email
                </button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
